import { useState } from "react";
import { Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const StatCard = ({ icon: Icon, iconCls, label, value, sub, subCls = "text-emerald-400", testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-white/5 bg-[#11141A] p-5 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-white/10"
  >
    <div className="flex items-center gap-4">
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${iconCls}`}>
        <Icon size={20} />
      </span>
      <div className="min-w-0">
        <p className="truncate text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
        <p className="font-display text-[22px] font-bold leading-tight text-white">{value}</p>
      </div>
    </div>
    {sub && <p className={`mt-3 text-xs ${subCls}`}>{sub}</p>}
  </div>
);

export const Panel = ({ title, action, children, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-white/5 bg-[#11141A] p-5">
    <div className="mb-4 flex items-center justify-between">
      <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

export const ViewAll = ({ onClick, to }) => (
  <a
    href={to}
    onClick={onClick}
    data-testid="view-all-button"
    className="rounded-lg border border-white/10 px-3.5 py-1.5 text-xs font-medium text-zinc-400 transition-colors hover:border-[#FFB800]/50 hover:text-[#FFB800]"
  >
    Ver todas
  </a>
);

export const StatusSelect = ({ value, options, onChange, testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="rounded-lg border border-white/10 bg-[#0A0B0D] px-2.5 py-1.5 text-xs font-semibold text-zinc-300 outline-none focus:border-[#FFB800]"
  >
    {options.map((o) => (
      <option key={o} value={o} className="bg-[#0A0B0D]">
        {o}
      </option>
    ))}
  </select>
);

export const TableShell = ({ head, children, testId }) => (
  <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#11141A]" data-testid={testId}>
    <table className="w-full min-w-[640px] text-left text-sm">
      <thead>
        <tr className="border-b border-white/5 text-[11px] uppercase tracking-wider text-zinc-500">
          {head.map((h) => (
            <th key={h} className="px-5 py-4 font-semibold">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-white/5">{children}</tbody>
    </table>
  </div>
);

export const PageHeader = ({ title, sub, children, testId }) => (
  <div className="mb-8 flex flex-wrap items-center justify-between gap-4" data-testid={testId}>
    <div>
      <h1 className="font-display text-3xl font-bold uppercase text-white">{title}</h1>
      {sub && <p className="mt-1 text-sm text-zinc-500">{sub}</p>}
    </div>
    {children}
  </div>
);

const QUOTE_SERVICES = [
  "Vazamento de Gás",
  "Instalação de Aquecedor",
  "Conversão de Fogão",
  "Tubulação de Gás",
  "Teste de Estanqueidade",
  "Laudo Técnico",
  "Manutenção Preventiva",
  "Instalação Industrial",
];

export const QuoteModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ client: "", service: QUOTE_SERVICES[0], value: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/quotes", { client: form.client, service: form.service, value: parseFloat(form.value) || 0 });
      toast.success("Orçamento criado com sucesso!");
      setForm({ client: "", service: QUOTE_SERVICES[0], value: "" });
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[#0A0B0D] px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none focus:border-[#FFB800]";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border-white/10 bg-[#11141A] text-white" data-testid="quote-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold uppercase">
            Novo <span className="italic text-[#FFB800]">Orçamento</span>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <input
            required
            data-testid="quote-client-input"
            placeholder="Nome do cliente *"
            value={form.client}
            onChange={(e) => setForm({ ...form, client: e.target.value })}
            className={inputCls}
          />
          <select
            data-testid="quote-service-select"
            value={form.service}
            onChange={(e) => setForm({ ...form, service: e.target.value })}
            className={inputCls}
          >
            {QUOTE_SERVICES.map((s) => (
              <option key={s} value={s} className="bg-[#0A0B0D]">
                {s}
              </option>
            ))}
          </select>
          <input
            required
            data-testid="quote-value-input"
            type="number"
            min="0"
            step="0.01"
            placeholder="Valor (R$) *"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            className={inputCls}
          />
          <button
            type="submit"
            disabled={saving}
            data-testid="quote-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FFB800] py-3.5 font-display text-sm font-bold uppercase tracking-wider text-black transition-transform hover:scale-[1.02] disabled:opacity-60"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <X size={0} className="hidden" />}
            {saving ? "Salvando..." : "Criar Orçamento"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
